// The playback engine shared by the /tutorial-demo player and the vertical reel: a clock,
// per-scene narration kept in step with it, and the overlays (cursor, click ripple,
// spotlight, scrolling) that are measured against the live DOM every frame.
import {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react'
import {Scene, Target, deriveState, easeInOut, makeTimeline} from './demoScript'

export const useDemoEngine = (scenes: Scene[], stageW: number, stageH: number) => {
  const timeline = useMemo(() => makeTimeline(scenes), [scenes])
  const {total, sceneAt, sceneStart} = timeline

  const [t, setT] = useState(0)
  const [playing, setPlaying] = useState(false)
  const [started, setStarted] = useState(false)
  const [muted, setMuted] = useState(false)
  // Re-render without advancing time — lets the overlays re-measure after a seek scrolls.
  const [, remeasure] = useState(0)

  const tRef = useRef(0)
  const playingRef = useRef(false)
  const stageRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const cursorRef = useRef<HTMLDivElement>(null)
  const rippleRef = useRef<HTMLDivElement>(null)
  const spotRef = useRef<HTMLDivElement>(null)
  // Scroll steps are side effects, not derived state: each fires once per pass through a scene.
  const firedScrolls = useRef<Set<string>>(new Set())
  const movePositions = useRef<Map<string, {x: number; y: number}>>(new Map())
  const audios = useMemo(
    () =>
      scenes.map((s) => {
        const a = new Audio(s.audio)
        a.preload = 'auto'
        return a
      }),
    [scenes]
  )

  const {index, local} = sceneAt(t)
  const scene = scenes[index]
  const state = deriveState(scene, local)

  // ── Clock ──
  // A timer rather than requestAnimationFrame: rAF stops in a background tab while the
  // narration keeps playing, so the picture would fall behind the voice. Hidden tabs throttle
  // timers to ~1/s, hence the generous dt cap.
  useEffect(() => {
    let last = performance.now()
    const tick = () => {
      const now = performance.now()
      const dt = Math.min(now - last, 1000)
      last = now
      if (playingRef.current) {
        const next = Math.min(tRef.current + dt, total)
        tRef.current = next
        setT(next)
        if (next >= total) {
          playingRef.current = false
          setPlaying(false)
        }
      }
    }
    const id = window.setInterval(tick, 16)
    return () => window.clearInterval(id)
  }, [total])

  // ── Narration: keep the current scene's clip in step with the clock ──
  useEffect(() => {
    audios.forEach((a, i) => {
      a.muted = muted
      if (i !== index || !playing) {
        if (!a.paused) a.pause()
        return
      }
      const want = local / 1000
      if (want >= (a.duration || scenes[i].audioMs / 1000)) {
        if (!a.paused) a.pause()
        return
      }
      if (Math.abs(a.currentTime - want) > 0.3) a.currentTime = want
      if (a.paused) a.play().catch(() => {})
    })
  }, [audios, scenes, index, local, playing, muted])

  useEffect(() => () => audios.forEach((a) => a.pause()), [audios])

  // ── Measure a target on the stage, in stage coordinates ──
  const measure = useCallback(
    (target: Target | null | undefined): DOMRect | null => {
      if (!target) return null
      if (typeof target !== 'string') return new DOMRect(target.x, target.y, 0, 0)
      const stage = stageRef.current
      const el = stage?.querySelector(target)
      if (!stage || !el) return null
      const sr = stage.getBoundingClientRect()
      const r = el.getBoundingClientRect()
      const k = sr.width / stageW
      return new DOMRect((r.left - sr.left) / k, (r.top - sr.top) / k, r.width / k, r.height / k)
    },
    [stageW]
  )

  // Scrolls the target into view inside whichever box actually scrolls it: the post
  // modal's body for fields in the editor, otherwise the page content.
  const scrollTo = useCallback((target: string, smooth: boolean) => {
    const page = contentRef.current
    if (!page) return
    if (target === 'top') {
      page.scrollTo({top: 0, behavior: smooth ? 'smooth' : 'auto'})
      return
    }
    const el = stageRef.current?.querySelector(target) as HTMLElement | null
    if (!el) return
    const box = (el.closest('.fb-modal-body') as HTMLElement | null) || page
    const k = box.getBoundingClientRect().height / box.clientHeight || 1
    const top = box.scrollTop + (el.getBoundingClientRect().top - box.getBoundingClientRect().top) / k - 20
    box.scrollTo({top, behavior: smooth ? 'smooth' : 'auto'})
  }, [])

  // ── Per-frame overlays: scroll effects, cursor, click ripple, spotlight ──
  useLayoutEffect(() => {
    scene.steps.forEach((st, i) => {
      if (!('scroll' in st) || st.at > local) return
      const key = `${index}:${i}`
      if (firedScrolls.current.has(key)) return
      firedScrolls.current.add(key)
      scrollTo(st.scroll, true)
    })

    const center = (r: DOMRect | null) => (r ? {x: r.x + r.width / 2, y: r.y + r.height / 2} : null)
    const moves = scene.steps.filter((st): st is Extract<Scene['steps'][number], {move: Target}> => 'move' in st)
    let pos = scene.cursorStart || {x: stageW / 2, y: stageH / 2}
    let prev = pos
    moves.forEach((mv, i) => {
      if (mv.at > local) return
      // A target that has since left the screen (a modal step that was replaced) keeps the
      // spot where it was last seen, so the cursor doesn't jump back to an older target.
      const key = `${index}:${i}`
      const seen = center(measure(mv.move))
      if (seen) movePositions.current.set(key, seen)
      const to = seen || movePositions.current.get(key) || prev
      const p = easeInOut(Math.min(1, (local - mv.at) / Math.max(mv.dur, 1)))
      pos = {x: prev.x + (to.x - prev.x) * p, y: prev.y + (to.y - prev.y) * p}
      prev = to
    })
    const lastClick = [...scene.steps].reverse().find((st) => 'click' in st && st.at <= local)
    const since = lastClick ? local - lastClick.at : Infinity
    if (cursorRef.current) {
      cursorRef.current.style.transform = `translate(${pos.x}px, ${pos.y}px) scale(${since < 160 ? 0.82 : 1})`
      cursorRef.current.style.opacity = state.outro || scene.hideCursor ? '0' : '1'
    }
    if (rippleRef.current) {
      const on = since < 500
      rippleRef.current.style.opacity = on ? String(1 - since / 500) : '0'
      rippleRef.current.style.transform = `translate(${pos.x}px, ${pos.y}px) scale(${on ? 0.4 + since / 400 : 0})`
    }

    const lastSpot = [...scene.steps].reverse().find((st) => 'spot' in st && st.at <= local) as {spot: string | null} | undefined
    const r = measure(lastSpot?.spot)
    if (spotRef.current) {
      const pad = 8
      spotRef.current.style.opacity = r ? '1' : '0'
      if (r) {
        Object.assign(spotRef.current.style, {
          left: `${r.x - pad}px`,
          top: `${r.y - pad}px`,
          width: `${r.width + pad * 2}px`,
          height: `${r.height + pad * 2}px`,
        })
      }
    }
  })

  const seek = useCallback(
    (ms: number) => {
      const next = Math.max(0, Math.min(ms, total - 1))
      tRef.current = next
      setT(next)
      // Replay this scene's scroll position instantly and mark earlier scrolls as done.
      const {index: i, local: l} = sceneAt(next)
      firedScrolls.current = new Set()
      const passed: string[] = []
      scenes[i].steps.forEach((st, k) => {
        if ('scroll' in st && st.at <= l) {
          firedScrolls.current.add(`${i}:${k}`)
          passed.push(st.scroll)
        }
      })
      // After the seeked frame has rendered (a timer, not rAF — rAF doesn't run in a
      // background tab), replay every scroll so both the page and an open modal land
      // where they would have been.
      window.setTimeout(() => {
        if (!passed.length) scrollTo('top', false)
        passed.forEach((target) => scrollTo(target, false))
        remeasure((n) => n + 1)
      }, 30)
    },
    [scenes, total, sceneAt, scrollTo]
  )

  const play = useCallback(() => {
    if (tRef.current >= total - 50) seek(0)
    setStarted(true)
    playingRef.current = true
    setPlaying(true)
  }, [seek, total])

  const pause = useCallback(() => {
    playingRef.current = false
    setPlaying(false)
  }, [])

  const toggle = useCallback(() => (playingRef.current ? pause() : play()), [play, pause])

  return {
    t, total, index, local, scene, state, sceneStart,
    playing, started, setStarted, muted, setMuted,
    play, pause, toggle, seek,
    stageRef, contentRef, cursorRef, rippleRef, spotRef,
  }
}
